import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { AbstractBasketComponent } from '@df/basket/abstract-basket.component';
import { ConfigResource } from '@df/catalog/config/config.resource';
import { IProductDataV3 } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { ICmsComponent, ICmsContent } from '@icms/core/content';
import { ICmsConfig } from '@icms/core/resource/config/icms-config';
import { LocalBasket } from '../../basket/basket.decorator';
import { Product } from '../../catalog/product/product.decorator';
import { ProtectionPlansItemComponent } from './item/protection-plans-item.component';
import { CMS_CONFIG_PROTECTION_PLANS, IICmsConfigProtectionPlansData, PROTECTION_PLANS_FIELDS } from './protection-plans.cms-config';

interface ProtectionPlanGroup {
    type: string;
    planIds: number[];
    products: Product<IProductDataV3>[];
}

interface ProtectionPlansComponentData {
    protectionPlansHeader?: string;
    assemblyPlansHeader?: string;
}

@Component({
    selector: 'protection-plans',
    templateUrl: './protection-plans.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, ProtectionPlansItemComponent, SizeClassDirective]
})
@ICmsComponent<ProtectionPlansComponent>({
    id: 'protection-plans',
    name: 'Protection Plans',
    description: 'Protection and assembly plans component',
    tags: ['basket', 'protection', 'plans'],
    fields: PROTECTION_PLANS_FIELDS
})
export class ProtectionPlansComponent extends AbstractBasketComponent implements OnInit {
    private productResource = inject(ProductResource);
    private configResource = inject(ConfigResource);

    private config?: ICmsConfig<IICmsConfigProtectionPlansData>;

    protected protectionPlanGroups: ProtectionPlanGroup[] = [];

    @ICmsContent({
        type: 'component'
    })
    component?: ProtectionPlansComponentData;

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.basket.subscribe(() => this.onBasketUpdate()));
        this.onBasketUpdate();

        this.configResource.loadModelByUrl(CMS_CONFIG_PROTECTION_PLANS).finally(() => {
            this.config = this.configResource.getModelByUrl(
                CMS_CONFIG_PROTECTION_PLANS
            ) as any as ICmsConfig<IICmsConfigProtectionPlansData>;
            this.changeDetectorRef.detectChanges();
        });
    }

    get protectionPlansIds(): number[] {
        return (this.basket as LocalBasket).protectionPlansIds;
    }

    get protectionPlansByType(): { [planType: string]: number[] } {
        return (this.basket as LocalBasket).protectionPlansByType;
    }

    getHeaderForType(planType: string): string {
        if (planType === 'protection') {
            const header = this.component?.protectionPlansHeader || this.config?.data?.content?.protectionPlansHeader || 'Protection Plans';
            return header;
        } else if (planType === 'assembly') {
            const header = this.component?.assemblyPlansHeader || this.config?.data?.content?.assemblyPlansHeader || 'Assembly Plans';
            return header;
        }
        return planType.charAt(0).toUpperCase() + planType.slice(1) + ' Plans';
    }

    private onBasketUpdate(): void {
        if (this.protectionPlansIds.length) {
            this.productResource.loadAllIds(this.protectionPlansIds, 3, { safe: true }).finally(() => {
                this.updateProtectionPlanGroups();
                this.detectChanges();
            });
        } else {
            this.protectionPlanGroups = [];
            this.detectChanges();
        }
    }

    private updateProtectionPlanGroups(): void {
        const plansByType = this.protectionPlansByType;
        const groups: ProtectionPlanGroup[] = [];

        Object.keys(plansByType).forEach(planType => {
            const planIds = plansByType[planType];

            if (planIds.length > 0) {
                const allProducts = this.productResource.getAllExisting(planIds);

                const filteredProducts = allProducts.filter(i => {
                    const inBasket = this.basket.byProduct(i.id);
                    const outOfStock = i.isOutOfStock;
                    const isProtectionPlan = (i.data as IProductDataV3)?.isProductProtectionPlan;

                    if (isProtectionPlan) {
                        return !inBasket;
                    }

                    return !inBasket && !outOfStock;
                }) as Product<IProductDataV3>[];

                if (filteredProducts.length > 0) {
                    groups.push({
                        type: planType,
                        planIds: planIds,
                        products: filteredProducts
                    });
                }
            }
        });

        this.protectionPlanGroups = groups;
    }

    itemTrackFn(index: number, item: BasketItem): number {
        return item.id;
    }
}
